import { FocusTrap } from 'focus-trap-react';
import { h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { AD_PAUSEROLL } from '../../../../core/pauseroll/constants';
import { USER_CLICK, USER_CLOSED_PAUSEROLL, USER_FULLSCREEN } from '../../../../types';
import { DIALOG_LAYER } from '../../../common/constants';
import { useFadeInComponent, useNewStore, usePlayerContext, useStyles } from '../../../common/hooks';
import { isKeyBackward, isKeyForward } from '../../../common/utils/focusTrap';
import { withBreakPoints } from '../../hoc';
import { MAIN_GRADIENT_AD, PRIMARY_TEXT_COLOR } from '../../theme/colors';
import { Button, Label } from '../common';
import PauserollResume from '../controls/pauserollresume';
import {
  ADPAUSE_SCREEN,
  ADPAUSE_SCREEN_BACKGROUND,
  ADPAUSE_SCREEN_CONTROLS_WRAPPER,
  MAIN_WRAPPER_PADDING
} from './styles';

import { AD_INFOS_LABEL_STYLE } from '../../../common/components/styles';

function AdPauseOverlay({ mobile, tablet }) {
  const player = usePlayerContext();
  const { isFullscreen } = useNewStore('ui', player);
  const { pauseRollIsDisplayable, pauseRollContent } = useNewStore('ad', player);
  const { isTv } = useNewStore('media', player);

  const setStyles = useStyles();

  const [shouldRender, visible] = useFadeInComponent(pauseRollIsDisplayable);

  const resumeButtonRef = useRef();
  useEffect(() => {
    if (visible) resumeButtonRef.current.children[0].focus();
  }, [pauseRollContent, pauseRollIsDisplayable, shouldRender, visible]);

  if (!shouldRender) return null;

  return (
    <FocusTrap
      active={visible}
      focusTrapOptions={{
        clickOutsideDeactivates: true,
        returnFocusOnDeactivate: false,
        isKeyForward,
        isKeyBackward
      }}
    >
      <div
        className="ftv-in-customizable adpause"
        style={{
          zIndex: DIALOG_LAYER,
          ...ADPAUSE_SCREEN,
          width: visible ? '100%' : '0px',
          height: visible ? '100%' : '0px',
          opacity: Number(visible),
          backgroundColor: visible ? 'black' : 'transparent',
          transition: visible
            ? 'background-color 0.5s ease-in, opacity 0.5s ease-in'
            : ''
        }}
      >
        <div
          style={{
            ...ADPAUSE_SCREEN_BACKGROUND,
            opacity: Number(visible),
            transition: visible
              ? 'background-image 2s ease-in, opacity 2s ease-in'
              : '',
            backgroundImage: pauseRollContent?.image ? `url("${pauseRollContent.image}")` : 'none',
            cursor: 'pointer'
          }}
          role="presentation"
        >
          <div style={{ backgroundImage: MAIN_GRADIENT_AD, width: '100%' }}>
            <div style={{ ...ADPAUSE_SCREEN_CONTROLS_WRAPPER, ...setStyles(MAIN_WRAPPER_PADDING) }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}
              >
                <Label
                  message="Publicité"
                  blur="medium"
                  style={{ ...AD_INFOS_LABEL_STYLE, userSelect: 'none' }}
                />
                <div role="presentation" style={{ display: 'flex' }}>
                  {(mobile || tablet) && pauseRollContent.click && (
                    <Label
                      message="+ d'infos"
                      onClick={() => player.adClick(AD_PAUSEROLL)}
                      styleText={{ color: PRIMARY_TEXT_COLOR }}
                      opaque
                    />
                  )}
                  <Button
                    hidden={isTv}
                    type="icon"
                    openingMode="click"
                    label="Fermer la publicité"
                    ariaLabel="Fermer la publicité"
                    forceLabel
                    disableFocus={!visible}
                    last
                    zone="bottom"
                    icon="close"
                    name="btn-close-adpause"
                    ariaHidden={false}
                    onClick={() => {
                      player.userEvents$.next({ action: USER_CLOSED_PAUSEROLL });
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <PauserollResume innerRef={resumeButtonRef} disableFocus={!visible} />
                <Button
                  hidden={isTv}
                  label={`${isFullscreen ? 'Quitter le plein écran' : 'Plein écran'} (f)`}
                  ariaLabel={isFullscreen ? 'Quitter le mode plein écran' : 'Plein écran'}
                  ariaHidden="false"
                  onClick={() => {
                    player.userEvents$.next({
                      action: USER_FULLSCREEN,
                      value: !isFullscreen,
                      source: USER_CLICK
                    });
                    player.fullscreen(!isFullscreen);
                  }}
                  last
                  xs
                  name="btn-fullscreen-adpause"
                  icon={isFullscreen ? 'fullscreen-on' : 'fullscreen-off'}
                  disableFocus={!visible}
                  type="icon"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </FocusTrap>
  );
}

export default withBreakPoints(AdPauseOverlay);
